import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import userService from '../../../services/userService';
import { AdminUser } from '../../../services/authService';

const UserList: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isSystemAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on the root users page
  const isRootUsersPage = location.pathname === '/admin/users';
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  // Sorting state
  const [sortField, setSortField] = useState<string>('email');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Fetch users with pagination
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers(
        pagination.currentPage,
        pagination.pageSize,
        sortField,
        sortOrder
      );
      
      console.log('Fetched users:', response);
      
      // Update users and pagination data
      setUsers(response.data);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch users when pagination or sorting changes
  useEffect(() => {
    fetchUsers();
  }, [pagination.currentPage, pagination.pageSize, sortField, sortOrder]);

  // Redirect if not system admin
  useEffect(() => {
    if (!isSystemAdmin()) {
      navigate('/admin/dashboard');
    }
  }, [isSystemAdmin, navigate]);

  const handleCreateUser = () => {
    navigate('/admin/users/create');
  };

  const handleBack = () => {
    navigate('/admin/dashboard', { replace: true });
  };

  const handleViewUser = (userId: string) => {
    console.log('Viewing user with ID:', userId);
    if (!userId) {
      console.error('User ID is undefined');
      return;
    }
    navigate(`/admin/users/${userId}`);
  };

  const handleEditUser = (userId: string) => {
    navigate(`/admin/users/${userId}/edit`);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        // Refresh the user list after deletion
        fetchUsers();
      } catch (err) {
        console.error('Failed to delete user:', err);
        alert('Failed to delete user. Please try again later.');
      }
    }
  };
  
  // Handle page change
  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage + 1 // MUI uses 0-based indexing, our API uses 1-based
    }));
  };
  
  // Handle rows per page change
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      currentPage: 1 // Reset to first page when changing page size
    }));
  };
  
  // Handle sort field change
  const handleSortFieldChange = (event: SelectChangeEvent) => {
    setSortField(event.target.value);
  };
  
  // Handle sort order change
  const handleSortOrderChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as 'asc' | 'desc');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isRootUsersPage && (
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Create User
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="sort-field-label">Sort By</InputLabel>
                <Select
                  labelId="sort-field-label"
                  value={sortField}
                  label="Sort By"
                  onChange={handleSortFieldChange}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="firstName">First Name</MenuItem>
                  <MenuItem value="lastName">Last Name</MenuItem>
                  <MenuItem value="role">Role</MenuItem>
                  <MenuItem value="active">Status</MenuItem>
                  <MenuItem value="createdAt">Created Date</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="sort-order-label">Order</InputLabel>
                <Select
                  labelId="sort-order-label"
                  value={sortOrder}
                  label="Order"
                  onChange={handleSortOrderChange}
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <TableContainer>
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Venues</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body1">No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow
                      key={user._id}
                      hover
                      onClick={() => handleViewUser(user._id)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                    >
                      <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            user.role === 'SYSTEM_ADMIN'
                              ? 'System Admin'
                              : user.role === 'VENUE_USER'
                                ? 'Venue User'
                                : 'Admin'
                          }
                          color={
                            user.role === 'SYSTEM_ADMIN'
                              ? 'secondary'
                              : user.role === 'VENUE_USER'
                                ? 'info'
                                : 'primary'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.role === 'VENUE_USER'
                          ? `${(user.assignedVenueIds ?? []).length}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.active ? 'Active' : 'Inactive'}
                          color={user.active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditUser(user._id);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            color="error" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(user._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={pagination.totalItems}
              page={pagination.currentPage - 1} // MUI uses 0-based indexing
              onPageChange={handlePageChange}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default UserList;
